import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  Divider,
  Paper,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  Pagination,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import Grid from "@mui/material/Grid";
import PaymentIcon from "@mui/icons-material/Payment";
import httpRequest from "../utils/httpRequest";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DownloadIcon from "@mui/icons-material/Download";
import Customer from "./Customer";
import TransactionModal from "./TransactionModal";

import CommonLedgerEntryForm from "./CommonLedgerEntryForm";
import FinancialYearSelector from "./FinancialYearSelector";
import CustomerFilterPopover from "./CustomerFilterPopover";
import CustomerCard from "./CustomerCard";
import LastCreditTransactionsModal from "./LastCreditTransactionsModal";

const CustomerTransactionsView = () => {
  const [syncing, setSyncing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [filters, setFilters] = useState({});
  const [customerPage, setCustomerPage] = useState(1);
  const [customerPageSize, setCustomerPageSize] = useState(6);
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const [commonLedgerOpen, setCommonLedgerOpen] = useState(false);
  const [creditTransactionsOpen, setCreditTransactionsOpen] = useState(false);
  const [activeCustomer, setActiveCustomer] = useState(null);
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState("all");
  const [locations, setLocations] = useState([]);
  const [customerMeta, setCustomerMeta] = useState({ totalPages: 1 });
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsPageSize, setTransactionsPageSize] = useState(5);
  const [transactionsMeta, setTransactionsMeta] = useState({
    data: [],
    totalPages: 1,
  });
  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: "",
  });
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    gst_number: "",
    account_number: "",
    mobile: "",
    address: "",
    email: "",
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    overall_credit: 0,
    overall_debit: 0,
    overall_balance: 0,
  });
  const [viewMode, setViewMode] = useState("card");
  const [nextAvailable, setNextAvailable] = useState(false);
  const [previousAvailable, setPreviousAvailable] = useState(false);

  const fetchCustomerData = async () => {
    try {
      const query = new URLSearchParams({
        page: customerPage,
        limit: customerPageSize,
        fy: selectedFY,
        ...filters,
      });

      if (!selectedFY) return;
      const res = await httpRequest(`customers/transactions?${query}`, "GET");

      setData(res.data);
      setCustomerMeta({
        totalPages: res.pagination.totalPages,
      });
      setSummary(res.summary);
    } catch (error) {
      console.error("Failed to fetch customer data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerData();
  }, [customerPage, customerPageSize, newCustomer, selectedFY, filters]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await httpRequest("service-location", "GET");
        setLocations(response || []);
      } catch (error) {
        console.error("Failed to fetch locations", error);
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    const fetchFinancialYears = async () => {
      try {
        const res = await httpRequest("invoice/getAllFinancialYears", "GET");
        const sorted = res.sort(
          (a, b) => new Date(b.start_date) - new Date(a.start_date)
        );
        setFinancialYears(sorted);
      } catch (error) {
        console.error("Failed to fetch financial years", error);
      }
    };
    fetchFinancialYears();
  }, []);

  const handleNextAccountNumber = useCallback(async () => {
    if (!filters.account_number) {
      return;
    }

    try {
      const response = await httpRequest(
        `customers/next-account-number?currentAccountNumber=${filters.account_number}`,
        'GET'
      );
      
      // Update the account number filter to the next one
      const newFilters = {
        ...filters,
        account_number: response.nextAccountNumber
      };
      setFilters(newFilters);
    } catch (error) {
      console.error('Error getting next account number:', error);
    }
  }, [filters, setFilters]);

  const handlePreviousAccountNumber = useCallback(async () => {
    if (!filters.account_number) {
      return;
    }

    try {
      const response = await httpRequest(
        `customers/previous-account-number?currentAccountNumber=${filters.account_number}`,
        'GET'
      );
      
      const newFilters = {
        ...filters,
        account_number: response.previousAccountNumber
      };
      setFilters(newFilters);
    } catch (error) {
      console.error('Error getting previous account number:', error);
    }
  }, [filters, setFilters]);

  // Check availability of next and previous account numbers
  const checkNavigationAvailability = useCallback(async () => {
    if (!filters.account_number) {
      setNextAvailable(false);
      setPreviousAvailable(false);
      return;
    }

    try {
      // Check next availability
      const nextResponse = await httpRequest(
        `customers/next-account-number?currentAccountNumber=${filters.account_number}`,
        'GET'
      );
      setNextAvailable(true);
    } catch (error) {
      setNextAvailable(false);
    }

    try {
      // Check previous availability
      const previousResponse = await httpRequest(
        `customers/previous-account-number?currentAccountNumber=${filters.account_number}`,
        'GET'
      );
      setPreviousAvailable(true);
    } catch (error) {
      setPreviousAvailable(false);
    }
  }, [filters.account_number]);

  // Check navigation availability when account number filter changes
  useEffect(() => {
    checkNavigationAvailability();
  }, [checkNavigationAvailability]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (filters.account_number && data.length === 1 && !event.ctrlKey && !event.metaKey) {
        if (event.key === 'n' && nextAvailable) {
          event.preventDefault();
          handleNextAccountNumber();
        } else if (event.key === 'p' && previousAvailable) {
          event.preventDefault();
          handlePreviousAccountNumber();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [filters.account_number, data.length, nextAvailable, previousAvailable, handleNextAccountNumber, handlePreviousAccountNumber]);

  const handleViewTransactions = async (
    customer,
    action = "",
    page = 1,
    pageSize = transactionsPageSize,
    dateRange = dateFilter
  ) => {
    try {
      let url = `customers/transactions/${customer.customer_id}?page=${page}&limit=${pageSize}&fy=${selectedFY}`;
      
      // Add date filter parameters if provided
      if (dateRange.startDate) {
        url += `&startDate=${dateRange.startDate}`;
      }
      if (dateRange.endDate) {
        url += `&endDate=${dateRange.endDate}`;
      }
      
      if (action) {
        url = `customers/transactions/${customer.customer_id}?page=${page}&limit=1000000000&fy=${selectedFY}&action=${action}`;
        // Don't apply date filters for downloads
      }

      const isDownload = action === "download";
      const res = await httpRequest(
        url,
        "GET",
        null,
        null,
        isDownload ? "blob" : "json"
      );

      if (!isDownload) {
        setSelectedCustomer(customer);
        setTransactionsMeta({
          data: res.data,
          totalPages: res.totalPages,
        });
        setTransactionsPage(page);
        setModalOpen(true);
      } else {
        const blob = new Blob([res], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `transactions-${customer.name || customer.customer_id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Failed to fetch transactions for", customer.customer_id, error);
    }
  };

  const handleTransactionPageChange = (page) => {
    handleViewTransactions(selectedCustomer, null, page, transactionsPageSize);
  };

  const handlePageSizeChange = (newSize) => {
    setTransactionsPageSize(newSize);
    handleViewTransactions(selectedCustomer, null, 1, newSize);
  };

  const handleEditTransaction = async (transactionId, updatedData) => {
    try {
      await httpRequest(`customers/transactions/${transactionId}`, "PUT", updatedData);
      handleViewTransactions(selectedCustomer, null, transactionsPage, transactionsPageSize);
      fetchCustomerData();
    } catch (error) {
      console.error("Failed to edit transaction:", error);
      throw error;
    }
  };

  const handleTransactionDeleted = () => {
    handleViewTransactions(selectedCustomer, null, transactionsPage, transactionsPageSize);
    fetchCustomerData();
  };

  const handleSyncCarryForward = async () => {
    setSyncing(true);
    try {
      await httpRequest('customer/sync-carry-forward', 'POST');
      setSnackbar({ open: true, message: 'Carry forward sync successful!', severity: 'success' });
      if (typeof fetchCustomerData === 'function') fetchCustomerData();
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Sync failed', severity: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const toggleViewMode = () => {
    const newMode = viewMode === "card" ? "table" : "card";
    setViewMode(newMode);
    setCustomerPage(1);
    setCustomerPageSize(newMode === "table" ? 25 : 6);
  };

  const handleReopenTransactionModal = () => {
    setModalOpen(true);
  };

  const handleDateFilterChange = (newDateFilter) => {
    setDateFilter(newDateFilter);
    // Refetch transactions with new date filter
    if (selectedCustomer) {
      handleViewTransactions(selectedCustomer, null, 1, transactionsPageSize, newDateFilter);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    // Reset date filter when modal closes
    setDateFilter({
      startDate: "",
      endDate: "",
    });
  };

  const downloadCustomerBalances = async () => {
    try {
      // Show loading state
      setSnackbar({ open: true, message: 'Preparing download...', severity: 'info' });
      
      if (!selectedFY) {
        setSnackbar({ open: true, message: 'Please select a financial year first', severity: 'warning' });
        return;
      }

      // Prepare query parameters for the export API
      const query = new URLSearchParams({
        fy: selectedFY,
        ...filters,
      });

      // Call backend API to generate and return Excel file
      const response = await httpRequest(
        `customers/export-balances?${query}`, 
        "GET", 
        null, 
        null, 
        "blob" // Expect blob response
      );

      if (!response) {
        setSnackbar({ open: true, message: 'No data available for export', severity: 'warning' });
        return;
      }

      // Create blob and download
      const blob = new Blob([response], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      // Generate filename with current date and financial year
      const currentDate = new Date().toISOString().split('T')[0];
      const fyText = selectedFY === 'all' ? 'All FY' : `FY ${selectedFY}`;
      const filename = `Customer_Balances_${fyText}_${currentDate}.xlsx`;

      // Create download link
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(link.href);
      
      setSnackbar({ open: true, message: 'Customer balances downloaded successfully!', severity: 'success' });
    } catch (error) {
      console.error('Failed to download customer balances:', error);
      setSnackbar({ open: true, message: 'Failed to download customer balances', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4">Customer Ledger</Typography>
          <CustomerFilterPopover
            filters={filters}
            setFilters={setFilters}
            locations={locations}
          />
          <Tooltip title={syncing ? 'Syncing...' : 'Sync Carry Forward'}>
            <span>
              <IconButton
                color="primary"
                onClick={handleSyncCarryForward}
                disabled={syncing}
                sx={{ bgcolor: syncing ? 'action.disabledBackground' : 'background.paper' }}
              >
                {syncing ? (
                  <CircularProgress size={22} thickness={5} />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
                    <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.17-.34 2.26-.93 3.17l1.46 1.46C19.41 15.1 20 13.62 20 12c0-4.42-3.58-8-8-8zm-6.07 2.93C4.59 8.9 4 10.38 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3c-3.31 0-6-2.69-6-6 0-1.17.34-2.26.93-3.17l-1.46-1.46z"/>
                  </svg>
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="View Last Credit Transactions">
            <IconButton
              color="primary"
              onClick={() => setCreditTransactionsOpen(true)}
              sx={{ bgcolor: 'background.paper' }}
            >
              <ReceiptIcon />
            </IconButton>
          </Tooltip>

        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControl sx={{ mr: 1 }} size="small">
            <FinancialYearSelector
              financialYears={financialYears}
              selectedFY={selectedFY}
              setSelectedFY={setSelectedFY}
            />
          </FormControl>
          <Customer
            newCustomer={newCustomer}
            setNewCustomer={setNewCustomer}
            locations={locations}
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setCommonLedgerOpen(true)}
            sx={{ ml: 1 }}
          >
            Common Ledger Entry
          </Button>
          <Tooltip title={viewMode === "card" ? "Switch to Table View" : "Switch to Card View"}>
            <IconButton onClick={toggleViewMode}>
              {viewMode === "card" ? <ViewListIcon /> : <ViewModuleIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box mb={2}>
        <Card variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={4}>
              <Typography variant="body2" color="text.secondary">
                Total Credit
              </Typography>
              <Typography variant="h6" sx={{ color: "success.main" }}>
                ₹{summary.overall_credit.toFixed(2)}
              </Typography>
            </Grid>
            <Grid size={4}>
              <Typography variant="body2" color="text.secondary">
                Total Debit
              </Typography>
              <Typography variant="h6" sx={{ color: "error.main" }}>
                ₹{summary.overall_debit.toFixed(2)}
              </Typography>
            </Grid>
            <Grid size={3}>
              <Typography variant="body2" color="text.secondary">
                Total Balance
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                ₹{summary.overall_balance.toFixed(2)}
              </Typography>
            </Grid>
            <Grid size={1} display="flex" justifyContent="flex-end">
              <Tooltip title="Download Customer Balances (Excel)">
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={downloadCustomerBalances}
                  sx={{ 
                    minWidth: 'auto',
                    px: 2,
                    py: 1,
                    fontSize: '0.75rem'
                  }}
                >
                  Export
                </Button>
              </Tooltip>
            </Grid>
          </Grid>
        </Card>
      </Box>
      
      {/* Navigation Buttons - only show when filtering by account number and single customer found */}
      {filters.account_number && 
       data.length === 1 && 
       viewMode === "card" && 
       Object.keys(filters).length === 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            mt: 1
          }}
        >
          <Tooltip title={previousAvailable ? "Go to previous account number (P)" : "No previous account number available"}>
            <IconButton 
              onClick={handlePreviousAccountNumber}
              disabled={!previousAvailable}
              color="primary"
              size="small"
              sx={{ 
                bgcolor: previousAvailable ? 'primary.main' : 'grey.300', 
                color: 'white',
                boxShadow: 1,
                '&:hover': {
                  bgcolor: previousAvailable ? 'primary.dark' : 'grey.300',
                  transform: previousAvailable ? 'scale(1.02)' : 'none'
                },
                transition: 'all 0.15s ease-in-out',
                width: 32,
                height: 32
              }}
            >
              <NavigateBeforeIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          
          <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5, fontSize: '0.75rem' }}>
            Account Navigation
          </Typography>
          
          <Tooltip title={nextAvailable ? "Go to next account number (N)" : "No next account number available"}>
            <IconButton 
              onClick={handleNextAccountNumber}
              disabled={!nextAvailable}
              color="primary"
              size="small"
              sx={{ 
                bgcolor: nextAvailable ? 'primary.main' : 'grey.300', 
                color: 'white',
                boxShadow: 1,
                '&:hover': {
                  bgcolor: nextAvailable ? 'primary.dark' : 'grey.300',
                  transform: nextAvailable ? 'scale(1.02)' : 'none'
                },
                transition: 'all 0.15s ease-in-out',
                width: 32,
                height: 32
              }}
            >
              <NavigateNextIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      
      {viewMode === "card" ? (
        <Grid container spacing={2}>
          {data.length > 0 ? (
            data.map((customer) => (
              <CustomerCard
                key={customer.id}
                customer={customer}
                handleViewTransactions={handleViewTransactions}
                setActiveCustomer={setActiveCustomer}
                setAddPaymentOpen={setAddPaymentOpen}
                fetchCustomerData={fetchCustomerData}
                selectedFY={selectedFY}
              />
            ))
          ) : (
            <Grid sx={{ textAlign: "center", py: 4 }} size={12}>
              <Typography variant="h6" color="text.secondary">
                No customers found.
              </Typography>
            </Grid>
          )}
        </Grid>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650, '& .MuiTableCell-root': { padding: '4px 8px', fontSize: '0.8rem' } }}>
            <TableHead>
              <TableRow sx={{ '& .MuiTableCell-head': { fontWeight: 'bold', backgroundColor: 'grey.100' } }}>
                <TableCell>Name</TableCell>
                <TableCell>Account Number</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Credit</TableCell>
                <TableCell>Debit</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length > 0 ? (
                data.map((customer) => (
                  <TableRow key={customer.id} sx={{ height: '36px' }}>
                    <TableCell>{customer.name}</TableCell>
                    <TableCell>{customer.account_number || "-"}</TableCell>
                    <TableCell>{customer.mobile || "-"}</TableCell>
                    <TableCell>{customer.email || "-"}</TableCell>
                    <TableCell>₹{customer.total_credit?.toFixed(2) || "0.00"}</TableCell>
                    <TableCell>₹{customer.total_debit?.toFixed(2) || "0.00"}</TableCell>
                    <TableCell>₹{customer.balance?.toFixed(2) || "0.00"}</TableCell>
                    <TableCell>
                      <Tooltip title="View Transactions">
                        <IconButton
                          size="small"
                          onClick={() => handleViewTransactions(customer)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Add Payment">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setActiveCustomer(customer);
                            setAddPaymentOpen(true);
                          }}
                        >
                          <PaymentIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No customers found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      <Box display="flex" alignItems="center" justifyContent="center" mt={3} gap={4}>
        <Pagination
          count={customerMeta.totalPages}
          page={customerPage}
          onChange={(e, value) => setCustomerPage(value)}
          color="primary"
          shape="rounded"
        />
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            Rows
          </Typography>
          <Select
            value={customerPageSize}
            onChange={(e) => {
              setCustomerPageSize(e.target.value);
              setCustomerPage(1);
            }}
            size="small"
          >
            {(viewMode === "table" ? [25, 50, 75, 100] : [6, 12, 18, 24]).map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
      <TransactionModal
        open={modalOpen}
        onClose={handleModalClose}
        transactions={transactionsMeta.data}
        customerName={selectedCustomer?.name}
        page={transactionsPage}
        totalPages={transactionsMeta.totalPages}
        pageSize={transactionsPageSize}
        onPageChange={handleTransactionPageChange}
        onPageSizeChange={handlePageSizeChange}
        onEditTransaction={handleEditTransaction}
        onTransactionDeleted={handleTransactionDeleted}
        onReopen={handleReopenTransactionModal}
        onDateFilterChange={handleDateFilterChange}
        customerId={selectedCustomer?.customer_id}
      />
      <CommonLedgerEntryForm
        open={addPaymentOpen}
        onClose={() => setAddPaymentOpen(false)}
        singleCustomer={activeCustomer}
        onSuccess={() => {
          fetchCustomerData();
        }}
      />
      <CommonLedgerEntryForm
        open={commonLedgerOpen}
        onClose={() => setCommonLedgerOpen(false)}
        onSuccess={() => {
          fetchCustomerData();
        }}
      />
      <LastCreditTransactionsModal
        open={creditTransactionsOpen}
        onClose={() => setCreditTransactionsOpen(false)}
        selectedFY={selectedFY}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerTransactionsView;